import { Button, Col, Collapse, Input, message, Radio, Row, Select, Spin, Tag, Upload } from 'antd';
import React, { useCallback, useEffect, useState } from 'react';
import { allowedFileCriteria, uploadAssetController } from '../../utils/fileUtils';
import { useTranslation } from 'react-i18next';
import { createNote, deleteNote, getNotes, uploadNoteDocument } from '../../services/notes';
import moment from 'moment-timezone';
import { getAsset, removeAsset } from '../../services/general';

const { TextArea } = Input;
const { Panel } = Collapse;

const allowedContentTypes = [
  'application/pdf',
  'image/png',
  'image/jpeg',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
];

const Notes = ({
  id,
  type,
  edit,
  title,
  showTagging,
  instructions,
  allowedTags = ['violent'],
  simultaneousUpload = false,
  defaultTags = [],
  onDocumentsChange = () => {},
}) => {
  const [text, setText] = useState('');
  const [tags, setTags] = useState(defaultTags);
  const [blocking, setBlocking] = useState(false);
  const [loading, setLoading] = useState(false);
  const [notes, setNotes] = useState([]);
  const [noteFiles, setNoteFiles] = useState([]);
  const { t } = useTranslation();

  const noteTypeMapper = {
    'reservation': t('notes.reservation'),
    'patient': t('notes.patient'),
    'medicalConsultation': t('notes.medicalConsultation', 'Consulta Médica'),
  };

  const noteTagsMapper = {
    violent: { text: t('notes.violent'), className: 'red' },
    blocked: { text: t('notes.blocked'), className: 'red' },
    interconsultation: { text: t('notes.interconsultation'), className: 'blue' },
  };

  const allSelectOptions = [
    { label: t('notes.violent', 'Paciente Violento'), value: 'violent' },
    { label: t('notes.interconsultation'), value: 'interconsultation' },
  ];

  const selectOptions = allSelectOptions.filter(option => allowedTags.includes(option.value));

  const handleChange = (event) => {
    setText(event.target.value);
  };

  const handleTagChange = (value) => {
    setTags(value);
    if (!value.includes('violent')) {
      setBlocking(false);
    }
  };

  const formatDateWithHour = (date) => {
    const dateMoment = moment.tz(date, 'UTC').format('DD-MM-YYYY HH:mm:ss');
    return dateMoment;
  };

  const fetchNotes = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getNotes({
        parentId: id,
        parentType: type,
      });
      setNotes(response);
    } catch (error) {
      console.error(error);
      message.error({
        content: t('notes.getError'),
        style: {
          marginTop: '30vh',
        },
      });
    } finally {
      setLoading(false);
    }
  }, [id, t, type]);

  useEffect(() => {
    fetchNotes();
  }, [fetchNotes, id, type]);

  const handleSaveSimultaneous = async () => {
    if (text.trim() === '' && noteFiles.length === 0) {
      message.error({
        content: 'Debe ingresar un comentario o adjuntar un archivo',
        style: {
          marginTop: '30vh',
        },
      });
      return;
    }

    setLoading(true);
    const noteBody = {
      parent_id: id,
      parent_type: type,
      text: text,
      tags: tags,
    };

    try {
      const noteResponse = await createNote(noteBody);
      const noteId = noteResponse?.id || (Array.isArray(noteResponse) && noteResponse[0]?.id);

      if (noteId) {
        const isInterconsultation = tags.includes('interconsultation');
        const noteAllowedTypes = isInterconsultation ? [
          'application/pdf',
          'image/png',
          'image/jpeg',
        ] : allowedContentTypes;

        for (const file of noteFiles) {
          const uploadResponse = await uploadNoteDocument({ note_id: noteId });
          const documentId = uploadResponse?.id || uploadResponse?.[0]?.id;
          if (documentId) {
            await uploadAssetController(
              {
                file,
                object_field: 'document',
                object_id: documentId,
                object_type: 'NotesDocuments',
              },
              noteAllowedTypes,
            );
          }
        }
      }

      message.success({
        content: t('notes.createSuccess'),
        style: {
          marginTop: '30vh',
        },
      });
      setText('');
      setTags(defaultTags);
      setNoteFiles([]);
    } catch (error) {
      console.error(error);
      message.error({
        content: t('notes.createError'),
        style: {
          marginTop: '30vh',
        },
      });
    } finally {
      setLoading(false);
    }
    fetchNotes();
    onDocumentsChange();
  };

  const handleSave = async () => {
    setLoading(true);
    const noteBody = {
      parent_id: id,
      parent_type: type,
      text: text,
      tags: blocking ? [...tags, 'blocked']: tags,
    };
    try {
      await createNote(noteBody);
      message.success({
        content: t('notes.createSuccess'),
        style: {
          marginTop: '30vh',
        },
      });
      setText('');
      setTags(defaultTags);
      setBlocking(false);
    } catch (error) {
      console.error(error);
      message.error({
        content: t('notes.createError'),
        style: {
          marginTop: '30vh',
        },
      });
    } finally {
      setLoading(false);
    }
    fetchNotes();
  };

  const handleDeleteNote = async (noteId) => {
    setLoading(true);
    try {
      await deleteNote({ noteId });
      message.success({
        content: t('notes.deleteSuccess'),
        style: {
          marginTop: '30vh',
        },
      });
      await fetchNotes();
      onDocumentsChange();
    } catch (error) {
      console.error('Error', error);
      message.error({
        content: t('notes.deleteError'),
        style: {
          marginTop: '30vh',
        },
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveDocument = async (documentId, event) => {
    setLoading(true);
    event.preventDefault();
    try {
      await removeAsset({
        field: 'document',
        id: documentId,
        type: 'NotesDocuments',
      });
      await fetchNotes();
      onDocumentsChange();
    } catch (error) {
      console.error('Error', error);
      message.error({
        content: t('notes.deleteDocumentError'),
        style: {
          marginTop: '30vh',
        },
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadDocument = async (noteDocument) => {
    setLoading(true);

    try {
      const url = await getAsset({
        field: 'document',
        id: noteDocument.id,
        type: 'NotesDocuments',
      });
      const link = document.createElement('a');
      link.href = url.document_url;
      link.download = noteDocument.document_file_name;
      link.setAttribute('target', '_blank');
      link.click();
    } catch (error) {
      message.error({
        content: t('notes.downloadDocumentError'),
        style: {
          marginTop: '30vh',
        },
      });
      console.error('Error', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddDocument = async (documentFile, note) => {
    const isInterconsultation = note.tags?.includes('interconsultation');
    const noteAllowedTypes = isInterconsultation ? [
      'application/pdf',
      'image/png',
      'image/jpeg',
    ] : allowedContentTypes;
    const errorMessage = isInterconsultation ? t('notes.documentImagePdfTypeError') : t('notes.documentTypeError');

    if (allowedFileCriteria(t, documentFile, 2, noteAllowedTypes, errorMessage)) {
      try {
        setLoading(true);
        const body = {
          note_id: note.id,
        };
        const response = await uploadNoteDocument(body);
        const documentId = response?.id || response?.[0]?.id;
        if (documentId) {
          await uploadAssetController(
            {
              file: documentFile,
              object_field: 'document',
              object_id: documentId,
              object_type: 'NotesDocuments',
            },
            noteAllowedTypes,
          );
          fetchNotes();
          onDocumentsChange();
          message.success({
            content: t('notes.addDocumentSuccess'),
            style: {
              marginTop: '30vh',
            },
          });
        }
      } catch (error) {
        console.error(error);
        message.error({
          content: t('notes.addDocumentError'),
          style: {
            marginTop: '30vh',
          },
        });
      } finally {
        setLoading(false);
      }
    }
    return false;
  };

  const renderNoteHeader = (note, index) => (
    <Row align="middle">
      <Col span={2}>
        <p className={`note-title mb-0 ${note.deleted_at ? 'deleted' : ''}`}>
          <strong>{t('notes.collapseHeader', { number: notes.length - index })}</strong>
        </p>
      </Col>
      <Col span={4}>
        <p
          className={`mb-0 ${note.deleted_at ? 'deleted' : ''}`}
          dangerouslySetInnerHTML={{ __html: t('notes.collapseUser', { fullname: note.fullname }) }}
        />
      </Col>
      <Col span={4}>
        <p className="mb-0">
          <strong>{t('notes.collapseNoteType')}</strong> {noteTypeMapper[type] || type || t('notes.defaultType', 'Nota')}
        </p>
      </Col>
      <Col span={6}>
        {showTagging && note.tags.length > 0 && (
          <div className="tags-container">
            <strong className='mr-5'>{t('notes.collapseTags')}</strong>
            {note.tags.map((tag, index) => (
              <Tag key={index} className={`cmt-tag mr-0 ${noteTagsMapper[tag]?.className}`}>
                {noteTagsMapper[tag]?.text || tag}
              </Tag>
            ))}
          </div>
        )}
      </Col>
      <Col span={6}>
        <p
          className={`mb-0 ${note.deleted_at ? 'deleted' : ''}`}
          dangerouslySetInnerHTML={{ __html: t('notes.collapseDate', { date: formatDateWithHour(note.created_at) }) }}
        />
      </Col>
      <Col span={1}>
        <Button
          type="primary"
          className="deleteButton cmt-button secondary non-outline"
          onClick={() => handleDeleteNote(note.id)}
          size="large"
          disabled={!edit || note.deleted_at}
        >
          <span className="icon-system-delete" />
        </Button>
      </Col>
    </Row>
  );

  return (
    <Spin className="cmt-spin" spinning={loading}>
      <Row justify="center" className="notes-wrapper">
        <Col offset={1} lg={23} xs={23}>
          <h2>{title || t('notes.title')}</h2>
          <p>{instructions || t('notes.instruction')}</p>
          {showTagging && (
            <div className="mb-10">
              <Row>
                <p className="mb-5 tag-label">Etiqueta</p>
              </Row>
              <Row align="middle">
                <Col span={6}>
                  <Select
                    mode="multiple"
                    allowClear
                    className="cmt-select"
                    placeholder="Agregar etiquetas"
                    disabled={!edit}
                    value={tags}
                    onChange={handleTagChange}
                    options={selectOptions}
                  />
                </Col>
                {!simultaneousUpload && allowedTags.includes('violent') && (
                  <>
                    <p className="ml-10 mb-0">Bloquear Atencion:</p>
                    <Radio.Group
                      className="cmt-radio"
                      onChange={(value) => setBlocking(value.target.value)}
                      value={blocking}
                      disabled={!tags.includes('violent')}
                    >
                      <Radio value={true}>Si</Radio>|<Radio value={false}>No</Radio>
                    </Radio.Group>
                  </>
                )}
              </Row>
            </div>
          )}
          <TextArea className="cmt-input" rows={5} value={text} onChange={handleChange} disabled={!edit} maxLength={600} showCount />
          
          {simultaneousUpload && (
            <Row className="mt-20" align="middle">
              <Col span={24}>
                <Upload
                  beforeUpload={(documentFile) => {
                    const isInterconsultation = tags.includes('interconsultation');
                    const noteAllowedTypes = isInterconsultation ? [
                      'application/pdf',
                      'image/png',
                      'image/jpeg',
                    ] : allowedContentTypes;
                    const errorMessage = isInterconsultation ? t('notes.documentImagePdfTypeError') : t('notes.documentTypeError');

                    if (allowedFileCriteria(t, documentFile, 2, noteAllowedTypes, errorMessage)) {
                      setNoteFiles(previousFiles => [...previousFiles, documentFile]);
                    }
                    return false;
                  }}
                  onRemove={(documentFile) => {
                    setNoteFiles(previousFiles => previousFiles.filter(item => item.uid !== documentFile.uid));
                  }}
                  fileList={noteFiles}
                  multiple
                  disabled={!edit}
                >
                  <Button className="cmt-button secondary" disabled={!edit}>
                    <i className="icon-system-plus mr-5" />
                    Adjuntar Archivo
                  </Button>
                </Upload>
                <p className="mt-10 mb-0 font-12 text-muted">
                  {tags.includes('interconsultation') ? 'Formatos permitidos: PDF, PNG, JPG (Máx. 2MB)' : t('notes.allowedFileTypes')}
                </p>
              </Col>
            </Row>
          )}

          <Col span={4} className="mt-20">
            {' '}
            <Button onClick={simultaneousUpload ? handleSaveSimultaneous : handleSave} className="cmt-button primary outline" disabled={!edit}>
              {t('notes.saveButton')}
            </Button>
          </Col>
        </Col>
        <Col offset={1} lg={23} xs={23} className="mt-30">
          {notes.map((note, index) => (
            <Collapse key={index} className={`mb-10 ${note.tags?.includes('violent') ? 'violent-background' : ''}`} collapsible="icon">
              <Panel header={renderNoteHeader(note, index)}>
                <p className={note.deleted_at ? 'deleted' : ''}>{note.text}</p>
                <p>
                  <strong>{t('notes.documentsTitle')}</strong>
                </p>
                {note.documents?.length > 0 ? (
                  <Row className="mt-30">
                    {note.documents.map((document, j) => (
                      <Col key={j}>
                        <Tag className={`cmt-tag`} onClose={(event) => handleRemoveDocument(document.id, event)} closable>
                          <span
                            className="label"
                            onClick={() => {
                              handleDownloadDocument(document);
                            }}
                          >
                            {document.document_file_name}
                          </span>
                        </Tag>
                      </Col>
                    ))}
                  </Row>
                ) : (
                  <p>{t('notes.noDocuments')}</p>
                )}
                <Row className="mt-30" align="middle">
                  <Col className="mr-10">
                    <Upload beforeUpload={(file) => handleAddDocument(file, note)} showUploadList={false} disabled={!edit || note.deleted_at}>
                      <Button className={`cmt-button secondary ${!edit || note.deleted_at ? 'disabled' : ''}`} disabled={!edit || note.deleted_at}>
                        <i className="icon-system-plus mr-5" />
                        {t('notes.documentButton')}
                      </Button>
                    </Upload>
                  </Col>
                  <Col>
                    <p className="mb-0">{t('notes.allowedFileTypes')}</p>
                    <p className="mb-0">{t('notes.maxFileSize')}</p>
                  </Col>
                </Row>
              </Panel>
            </Collapse>
          ))}
        </Col>
      </Row>
    </Spin>
  );
};

export default Notes;
